import { useEffect, useState } from 'react';
import { api, ApiError } from '@/api/client';
import { usePagedList } from '../shipments/api';
import type { OrderDetail } from '@/api/types';
import type { ExceptionItem, ExceptionPage, ExceptionStats } from './types';

type Q = Record<string, string | number | undefined>;

export type ExceptionEvidence = { evidenceId: string; id: string; label: string; capturedAt: string; fileName: string; fileUrl: string; fileSize: number | null };

export function useExceptionEvidence(orderId: string | undefined, exceptionId: string) {
  const [state, setState] = useState<{ items: ExceptionEvidence[]; loading: boolean; error?: Error }>({ items: [], loading: !!orderId });
  useEffect(() => {
    if (!orderId) return;
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: undefined }));
    api.get<ExceptionEvidence[]>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}/evidence`, undefined, controller.signal)
      .then((items) => setState({ items, loading: false }))
      .catch((cause) => { if ((cause as Error).name !== 'AbortError') setState((current) => ({ ...current, loading: false, error: cause as Error })); });
    return () => controller.abort();
  }, [orderId, exceptionId]);
  return state;
}

/** 列表：GET /api/order-exceptions（status / type / responsibility / stockImpact / orderId / keyword + 分页） */
export function useExceptionList(query: Q, pageSize: number, append: boolean, urlPage = 1) {
  return usePagedList<ExceptionItem, ExceptionPage>('/api/order-exceptions', query, pageSize, append, urlPage);
}

/**
 * 状态视图的计数。接口的 stats 是按「当前条件（含 status）」算的，切到「已处理」时待处理就成了 0；
 * 所以另发一个 status=all、pageSize=1 的请求，只取 stats，其余筛选条件与列表一致。
 */
export function useExceptionCounts(query: Q, refreshVersion = 0) {
  const [stats, setStats] = useState<ExceptionStats>();
  const key = JSON.stringify(query);
  useEffect(() => {
    const c = new AbortController();
    api
      .get<ExceptionPage>('/api/order-exceptions', { ...JSON.parse(key), status: 'all', page: 1, pageSize: 1 }, c.signal)
      .then((d) => setStats(d.stats))
      .catch(() => undefined);
    return () => c.abort();
  }, [key, refreshVersion]);
  return stats;
}

/** 手机类型 chips 的全局计数；来源为同一可见范围的 V2 只读责任统计。 */
export function useExceptionTypeCounts(enabled: boolean, refreshVersion = 0) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    void api.get<{ byType: { key: string; count: number }[] }>('/api/v2/exceptions/responsibility', { range: 'all' }, controller.signal)
      .then((result) => setCounts(Object.fromEntries(result.byType.map((row) => [row.key, row.count]))))
      .catch(() => undefined);
    return () => controller.abort();
  }, [enabled, refreshVersion]);
  return counts;
}

type DetailState = {
  item?: ExceptionItem;
  /** 同一订单的其他异常 */
  siblings: ExceptionItem[];
  /** V2 订单详情（取批次行发货 / 破损数、组件用量规则和授权金额）；取不到就不显示那一块 */
  order?: OrderDetail;
  loading: boolean;
  error?: Error;
};

/**
 * 异常详情。老系统没有 GET /api/order-exceptions/:id，用 orderId 过滤取这张订单的全部异常（一张单通常只有几条），
 * 再按 id 找本条 —— 所以详情链接要带 ?order=<orderId>。从列表点进来时先用 seed 秒开，再静默刷新。
 */
export function useExceptionDetail(id: string, orderId?: string, seed?: ExceptionItem) {
  const initial = seed?.id === id ? seed : undefined;
  const [state, setState] = useState<DetailState>({ item: initial, siblings: [], loading: true });
  const [nonce, setNonce] = useState(0);
  const oid = orderId || initial?.order.id;
  useEffect(() => {
    if (!oid) {
      setState({ siblings: [], loading: false, error: new ApiError('链接不完整：缺少订单参数（?order=），请从异常列表重新打开', 400) });
      return;
    }
    const c = new AbortController();
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    (async () => {
      try {
        const page = await api.get<ExceptionPage>('/api/order-exceptions', { orderId: oid, status: 'all', pageSize: 100 }, c.signal);
        const item = page.items.find((x) => x.id === id);
        if (!item) throw new ApiError('没有找到这条异常，可能已被删除，或当前账号看不到这张订单', 404);
        if (!alive) return;
        const siblings = page.items.filter((x) => x.id !== id);
        setState((s) => ({ ...s, item, siblings }));
        try {
          const order = await api.get<OrderDetail>(`/api/v2/orders/${encodeURIComponent(oid)}`, undefined, c.signal);
          if (alive) setState({ item, siblings, order, loading: false });
        } catch (e) {
          if ((e as Error).name === 'AbortError') return;
          if (alive) setState({ item, siblings, loading: false });
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        if (alive) setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    })();
    return () => {
      alive = false;
      c.abort();
    };
  }, [id, oid, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}

export const exceptionHref = (e: { id: string; orderId?: string; order?: { id: string } }) => `/exceptions/${e.id}?order=${e.order?.id || e.orderId}`;
